/**
 * `AIRealtimeRoleCatalog.ts` — port of Unity's `AI/AIRealtimeRoleCatalog.cs`.
 *
 * Loads role personas from `/AI/roles/index.json` (+ the referenced `*.md` files). Loader only —
 * unlike the personality catalog it does NOT self-register; the caller (interface model /
 * orchestrator) passes the returned list to `setRoleDefinitions` in `agentPrompts.ts`.
 *
 * Web deviation: the manifest is REQUIRED (like Unity's Android path) — the browser cannot
 * directory-scan the public root, so a role file not listed in `index.json` is invisible.
 */

import { AIRealtimeRoleDefinition } from "./aiRealtimeContentTypes";
import { splitList, tryParseFrontmatter } from "./aiRealtimeMarkdownFrontmatter";
import { publicUrl } from "@core/publicUrl";
import { normalizeAgentPromptFileName } from "@models/data/agentPromptsDto";
import type { AgentPromptSource } from "./IAIPromptStoreModel";

const K_ROLES_ROOT = publicUrl("AI/roles");
const K_MANIFEST_PATH = `${K_ROLES_ROOT}/index.json`;

interface RoleManifestEntry {
  readonly id?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly file?: string;
  readonly tools?: string[];
}

/**
 * @param prompts The deployment's PUBLISHED role files (see
 * `IAIPromptStoreModel`), which stand in for the copy that shipped in the build.
 * The MANIFEST is never publishable — which roles
 * exist, and their ids, stay the shipped ones — so a published file can change a
 * role's persona but can never add, remove, or rename one. That is what keeps the
 * active-role id resolving across an edit.
 *
 * **`description` is FRONTMATTER-AUTHORITATIVE** (2026-08-14), like `tools:`
 * beside it: `roles/index.json` used to carry a duplicate that won this
 * `firstNonEmpty`, so the line in the file was dead text and editing it — by
 * upload, or in the AI debug panel — silently did nothing. The manifest no
 * longer sets the field, so the same precedence now lands on the file and there
 * is ONE source of truth for it. Do NOT re-add `description` to the manifest:
 * the shipped values were byte-identical duplicates, and putting one back makes
 * every file's line dead again with no signal anywhere.
 *
 * @returns the roles, or **null when the manifest or one of its referenced
 * files could not be read** — a transport failure, a non-2xx, unparseable JSON,
 * or an empty file. That is deliberately distinct from `[]` (a manifest that
 * genuinely lists no roles): the caller keeps its previous catalog on null,
 * because installing a partial/empty live catalog can remove the active persona
 * and make the role tool-gate fail OPEN.
 */
export async function loadAvailableRolesAsync(
  prompts?: AgentPromptSource | null,
): Promise<AIRealtimeRoleDefinition[] | null> {
  let entries: RoleManifestEntry[];
  try {
    // `no-cache` so a Reset re-reads `public/AI/**` from the server rather than a
    // memory-cached copy from earlier in the same session.
    const response = await fetch(K_MANIFEST_PATH, { cache: "no-cache" });
    if (!response.ok) {
      console.warn(`[AIRealtimeRoleCatalog] Failed to read ${K_MANIFEST_PATH}: HTTP ${response.status}`);
      return null;
    }

    const manifest = (await response.json()) as { roles?: RoleManifestEntry[] };
    entries = manifest.roles ?? [];
  } catch (e) {
    console.warn(`[AIRealtimeRoleCatalog] Failed to parse ${K_MANIFEST_PATH}:`, e);
    return null;
  }

  const roles: AIRealtimeRoleDefinition[] = [];
  for (const entry of entries) {
    if (!entry || !entry.file || !entry.file.trim()) {
      continue;
    }

    const role = await tryLoadRoleAsync(entry, prompts);
    if (!role) return null;
    roles.push(role);
  }

  return roles;
}

async function tryLoadRoleAsync(
  entry: RoleManifestEntry,
  prompts?: AgentPromptSource | null,
): Promise<AIRealtimeRoleDefinition | null> {
  const fileName = normalizeAgentPromptFileName(entry.file);
  if (!fileName) {
    return null;
  }

  // Keyed on the BASENAME, the same value the fetch below uses — see the note in
  // the skill catalog.
  const published = prompts?.getPromptText("roles", fileName) ?? null;

  let markdown: string;
  if (published !== null) {
    markdown = published;
  } else {
    try {
      const response = await fetch(`${K_ROLES_ROOT}/${fileName}`, { cache: "no-cache" });
      if (!response.ok) {
        console.warn(`[AIRealtimeRoleCatalog] Failed to read '${K_ROLES_ROOT}/${fileName}': HTTP ${response.status}`);
        return null;
      }

      markdown = await response.text();
    } catch (e) {
      console.warn(`[AIRealtimeRoleCatalog] Failed to read '${K_ROLES_ROOT}/${fileName}':`, e);
      return null;
    }
  }

  if (!markdown.trim()) {
    return null;
  }

  const { fields, body } = tryParseFrontmatter(markdown);
  const baseName = fileName.replace(/\.md$/i, "");

  const rawId = firstNonEmpty(entry.id, fields["id"], baseName);
  const id = rawId ? rawId.toLowerCase() : "";
  const displayName = firstNonEmpty(entry.displayName, fields["name"], fields["displayname"], baseName) ?? "";
  const description = firstNonEmpty(entry.description, fields["description"]);
  const manifestTools = (entry.tools ?? []).filter((tool) => tool && tool.trim());
  const tools = manifestTools.length > 0 ? manifestTools : splitList(fields["tools"]);

  return new AIRealtimeRoleDefinition(
    id,
    displayName,
    fileName,
    body,
    description,
    tools,
    markdown,
    published !== null,
  );
}

function firstNonEmpty(...values: Array<string | undefined | null>): string | null {
  for (const value of values) {
    if (value && value.trim()) {
      return value.trim();
    }
  }

  return null;
}
